import { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { LayoutDashboard, Users2, Briefcase, KeyRound, Clock, ShieldCheck, HardDrive, AlertTriangle } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";

interface StorageStats {
  totalSizeBytes: number;
  limitBytes: number;
  limitReached: boolean;
}

function formatSize(bytes: number) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function StatsGrid({ stats }: { stats: any[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat, idx) => {
        const Icon = stat.icon;
        return (
          <div key={idx} className="p-6 border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl shadow-sm flex items-center gap-4">
            <div className={`p-3.5 rounded-xl ${stat.color}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{stat.label}</p>
              <h4 className="text-2xl font-bold mt-1 text-foreground">{stat.value}</h4>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StorageWidget({ stats }: { stats: StorageStats | null }) {
  if (!stats) {
    return <div className="text-xs text-muted-foreground italic py-3">Loading storage metrics...</div>;
  }
  const storagePercentage = Math.min(100, (stats.totalSizeBytes / stats.limitBytes) * 100);
  return (
    <div className="space-y-3 pt-1 text-xs">
      <div className="flex justify-between items-center font-semibold">
        <span className="text-muted-foreground">
          Used: {formatSize(stats.totalSizeBytes)} of {formatSize(stats.limitBytes)}
        </span>
        <span className={stats.limitReached ? "text-destructive font-bold" : "text-brand-primary"}>
          {storagePercentage.toFixed(2)}%
        </span>
      </div>
      <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${stats.limitReached
            ? "bg-destructive"
            : storagePercentage >= 90
              ? "bg-amber-500"
              : "bg-brand-primary"
            }`}
          style={{ width: `${storagePercentage}%` }}
        ></div>
      </div>
      {stats.limitReached && (
        <div className="flex items-center gap-1.5 p-2 rounded-lg bg-destructive/10 text-destructive text-[11px] font-semibold">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>
            R2 Storage Limit Reached! Expand bucket size or{" "}
            <Link to="/media" className="underline font-bold hover:text-destructive/80">
              delete files
            </Link>.
          </span>
        </div>
      )}
      {!stats.limitReached && storagePercentage >= 90 && (
        <div className="flex items-center gap-1.5 p-2 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-500 text-[11px] font-semibold">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>
            Warning: R2 Storage utilization exceeds 90%.{" "}
            <Link to="/media" className="underline font-bold hover:text-amber-700">
              Manage files
            </Link>.
          </span>
        </div>
      )}
    </div>
  );
}

export function AdminDashboardTab() {
  const { content } = useOutletContext<{ content: any }>() || {};
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);

  useEffect(() => {
    adminFetch("/api/admin/storage-stats", {})
      .then((data) => {
        if (data?.success) setStorageStats(data);
      })
      .catch((err) => console.error("Failed to load storage stats:", err));
  }, []);

  const { timeline = [], hobbies = [], causes = [], socials = [], blogs = [] } = content || {};
  const timelineCount = timeline.length;
  const hobbiesCount = hobbies.length;
  const causesCount = causes.length;
  const socialsCount = socials.length;
  const blogsCount = blogs.length;

  const stats = [
    { label: "Timeline Nodes", value: timelineCount, icon: Briefcase, color: "text-blue-500 bg-blue-500/10" },
    { label: "Hobbies & Interests", value: hobbiesCount, icon: KeyRound, color: "text-purple-500 bg-purple-500/10" },
    { label: "Community Values", value: causesCount, icon: Users2, color: "text-emerald-500 bg-emerald-500/10" },
    { label: "Social Connections", value: socialsCount, icon: ShieldCheck, color: "text-amber-500 bg-amber-500/10" }
  ];

  return (
    <div className="space-y-8 animate-fade-in text-left">
      <div>
        <h2 className="text-3xl font-extrabold tracking-tight text-foreground flex items-center gap-3">
          <LayoutDashboard className="h-8 w-8 text-brand-primary" />
          <span>Dashboard</span>
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          Overview of your portfolio site's content database and active configurations.
        </p>
      </div>

      <StatsGrid stats={stats} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
            <Clock className="h-5 w-5 text-brand-primary" />
            <span>Quick Summary</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm pt-2">
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-semibold">BLOG POSTS</p>
              <h4 className="text-xl font-bold text-foreground">{blogsCount} posts</h4>
            </div>
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-semibold">DATABASE MODE</p>
              <h4 className="text-xl font-bold text-foreground">SQLite / D1</h4>
            </div>
          </div>
        </div>

        <div className="border border-border/60 bg-card/65 backdrop-blur-md rounded-2xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-foreground flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-brand-primary" />
              <span>Media Storage Utilization</span>
            </div>
            <Link to="/media" className="text-xs text-brand-primary hover:underline font-semibold">
              Open Media Library →
            </Link>
          </h3>
          <StorageWidget stats={storageStats} />
        </div>
      </div>
    </div>
  );
}

export default AdminDashboardTab;
